<template>
  <aside
    class="bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out"
    :class="[
      props.isCollapsed ? 'w-16' : 'w-64',
      'lg:relative lg:translate-x-0',
      'lg:translate-x-0 -translate-x-full',
    ]"
  >
    <!-- Sidebar Header -->
    <div class="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
      <h2 v-if="!props.isCollapsed" class="text-lg font-semibold text-gray-900 dark:text-gray-50">Navigation</h2>
      <button
        @click="$emit('toggle', !props.isCollapsed)"
        class="p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors duration-200"
        :aria-label="props.isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      >
        <ChevronLeftIcon v-if="!props.isCollapsed" class="w-5 h-5" />
        <ChevronRightIcon v-else class="w-5 h-5" />
      </button>
    </div>

    <!-- Navigation Content -->
    <nav class="flex-1 overflow-y-auto py-4">
      <div class="px-3 space-y-1">
        <!-- Home Link -->
        <router-link
          to="/"
          class="nav-link"
          :class="route.path === '/' ? 'nav-link-active' : 'nav-link-inactive'"
          :title="props.isCollapsed ? 'Home' : ''"
        >
          <HomeIcon class="w-5 h-5 flex-shrink-0" />
          <span v-if="!props.isCollapsed" class="ml-3">Home</span>
        </router-link>

        <!-- Information Section -->
        <div class="space-y-1">
          <button
            @click="toggleCategory('Information')"
            class="nav-link w-full justify-between"
            :class="[
              expandedCategories.includes('Information') ? 'text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400',
              'hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800',
            ]"
          >
            <div class="flex items-center">
              <DocumentTextIcon class="w-5 h-5 mr-2" />
              <span class="font-medium">Information</span>
            </div>
            <ChevronDownIcon
              v-if="!props.isCollapsed"
              class="w-4 h-4 transition-transform duration-200"
              :class="expandedCategories.includes('Information') ? 'rotate-180' : ''"
            />
          </button>
          
          <div v-if="expandedCategories.includes('Information') && !props.isCollapsed" class="ml-8 space-y-1">
            <router-link
              :to="{ name: 'information', params: { section: 'app-overview' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'app-overview' }"
            >
              App Overview
            </router-link>
            <router-link
              :to="{ name: 'information', params: { section: 'app-dependencies' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'app-dependencies' }"
            >
              App Dependencies
            </router-link>
            <router-link
              :to="{ name: 'information', params: { section: 'app-structure' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'app-structure' }"
            >
              App Structure
            </router-link>
          </div>
        </div>

        <!-- Standards Section -->
        <div class="space-y-1">
          <button
            @click="toggleCategory('Standards')"
            class="nav-link w-full justify-between"
            :class="[
              expandedCategories.includes('Standards') ? 'text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400',
              'hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800',
            ]"
          >
            <div class="flex items-center">
              <DocumentTextIcon class="w-5 h-5 mr-2" />
              <span class="font-medium">Standards</span>
            </div>
            <ChevronDownIcon
              v-if="!props.isCollapsed"
              class="w-4 h-4 transition-transform duration-200"
              :class="expandedCategories.includes('Standards') ? 'rotate-180' : ''"
            />
          </button>
          
          <div v-if="expandedCategories.includes('Standards') && !props.isCollapsed" class="ml-8 space-y-1">
            <router-link
              :to="{ name: 'standards', params: { section: 'overview' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'overview' }"
            >
              Overview
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'api-routing' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'api-routing' }"
            >
              API Routing
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'code-quality' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'code-quality' }"
            >
              Code Quality
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'database' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'database' }"
            >
              Database
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'deployment' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'deployment' }"
            >
              Deployment
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'documentation' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'documentation' }"
            >
              Documentation
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'error-handling' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'error-handling' }"
            >
              Error Handling
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'file-folder-naming' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'file-folder-naming' }"
            >
              File & Folder Naming
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'github-committing' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'github-committing' }"
            >
              Github Committing
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'logging-monitoring' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'logging-monitoring' }"
            >
              Logging & Monitoring
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'performance' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'performance' }"
            >
              Performance
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'security' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'security' }"
            >
              Security
            </router-link>
            <router-link
              :to="{ name: 'standards', params: { section: 'testing' } }"
              class="nav-link text-sm"
              :class="{ 'active': route.params.section === 'testing' }"
            >
              Testing
            </router-link>
          </div>
        </div>

        <!-- Documentation Section -->
        <div class="space-y-1">
          <button
            @click="toggleCategory('Documentation')"
            class="nav-link w-full justify-between"
            :class="[
              expandedCategories.includes('Documentation') ? 'text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400',
              'hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800',
            ]"
          >
            <div class="flex items-center">
              <DocumentTextIcon class="w-5 h-5 mr-2" />
              <span class="font-medium">Documentation</span>
            </div>
            <ChevronDownIcon
              v-if="!props.isCollapsed"
              class="w-4 h-4 transition-transform duration-200"
              :class="expandedCategories.includes('Documentation') ? 'rotate-180' : ''"
            />
          </button>
          
          <div v-if="expandedCategories.includes('Documentation') && !props.isCollapsed" class="ml-8 space-y-1">
            
            <!-- Backend Section -->
            <div class="space-y-1">
              <button
                @click="toggleSubCategory('Backend')"
                class="nav-link w-full justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span>Backend</span>
                <ChevronDownIcon 
                  v-if="!expandedSubCategories.includes('Backend')" 
                  class="w-4 h-4 transition-transform" 
                />
                <ChevronUpIcon 
                  v-else 
                  class="w-4 h-4 transition-transform" 
                />
              </button>
              
              <div v-if="expandedSubCategories.includes('Backend')" class="ml-4 space-y-1">
                                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-overview' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-overview' }"
                    >
                      Overview
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-structure' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-structure' }"
                    >
                      Structure
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-config' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-config' }"
                    >
                      Config
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-controllers' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-controllers' }"
                    >
                      Controllers
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-data' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-data' }"
                    >
                      Data
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-docs' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-docs' }"
                    >
                      Docs
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-emails' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-emails' }"
                    >
                      Emails
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-keys' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-keys' }"
                    >
                      Keys
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-locales' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-locales' }"
                    >
                      Locales
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-logs' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-logs' }"
                    >
                      Logs
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-middleware' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-middleware' }"
                    >
                      Middelware
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-migrations' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-migrations' }"
                    >
                      Migrations
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-models' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-models' }"
                    >
                      Models
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-public' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-public' }"
                    >
                      Public
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-routes' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-routes' }"
                    >
                      Routes
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-scripts' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-scripts' }"
                    >
                      Scripts
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-services' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-services' }"
                    >
                      Services
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-socket' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-socket' }"
                    >
                      Socket
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-tests' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-tests' }"
                    >
                      Tests
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-utilities' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-utilities' }"
                    >
                      Utilities
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-utils' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-utils' }"
                    >
                      Utils
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'backend-workers' } }"
                      class="nav-link text-sm"
                      :class="{ 'active': route.params.section === 'backend-workers' }"
                    >
                      Workers
                    </router-link>
              </div>
            </div>

            <!-- COFE Frontend Section -->
            <div class="space-y-1">
              <button
                @click="toggleSubCategory('COFE Frontend')"
                class="nav-link w-full justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span>COFE Frontend</span>
                <ChevronDownIcon 
                  v-if="!expandedSubCategories.includes('COFE Frontend')" 
                  class="w-4 h-4 transition-transform" 
                />
                <ChevronUpIcon 
                  v-else 
                  class="w-4 h-4 transition-transform" 
                />
              </button>
              
              <div v-if="expandedSubCategories.includes('COFE Frontend')" class="ml-4 space-y-1">
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'cofe-overview' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'cofe-overview' }"
                >
                  Overview
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'cofe-structure' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'cofe-structure' }"
                >
                  Structure
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'cofe-cypress' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'cofe-cypress' }"
                >
                  Cypress
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'cofe-dist' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'cofe-dist' }"
                >
                  Dist
                </router-link>
                
                <!-- Src dropdown -->
                <div class="space-y-1">
                  <button
                    @click="toggleSubCategory('COFE-Src')"
                    class="nav-link w-full justify-between text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    <span>Src</span>
                    <ChevronDownIcon 
                      v-if="!expandedSubCategories.includes('COFE-Src')" 
                      class="w-3 h-3 transition-transform" 
                    />
                    <ChevronUpIcon 
                      v-else 
                      class="w-3 h-3 transition-transform" 
                    />
                  </button>
                  
                  <div v-if="expandedSubCategories.includes('COFE-Src')" class="ml-4 space-y-1">
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-overview' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-overview' }"
                    >
                      Src Overview
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-assets' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-assets' }"
                    >
                      Assets
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-components' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-components' }"
                    >
                      Components
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-router' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-router' }"
                    >
                      Router
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-services' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-services' }"
                    >
                      Services
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-store' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-store' }"
                    >
                      Store
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'cofe-src-views' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'cofe-src-views' }"
                    >
                      Views
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- SENTRAFE Frontend Section -->
            <div class="space-y-1">
              <button
                @click="toggleSubCategory('SENTRAFE Frontend')"
                class="nav-link w-full justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span>SENTRAFE Frontend</span>
                <ChevronDownIcon 
                  v-if="!expandedSubCategories.includes('SENTRAFE Frontend')" 
                  class="w-4 h-4 transition-transform" 
                />
                <ChevronUpIcon 
                  v-else 
                  class="w-4 h-4 transition-transform" 
                />
              </button>
              
              <div v-if="expandedSubCategories.includes('SENTRAFE Frontend')" class="ml-4 space-y-1">
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sentrafe-overview' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sentrafe-overview' }"
                >
                  Overview
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sentrafe-structure' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sentrafe-structure' }"
                >
                  Structure
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sentrafe-cypress' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sentrafe-cypress' }"
                >
                  Cypress
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sentrafe-docs' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sentrafe-docs' }"
                >
                  Docs
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sentrafe-plugins' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sentrafe-plugins' }"
                >
                  Plugins
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sentrafe-scripts' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sentrafe-scripts' }"
                >
                  Scripts
                </router-link>
                
                <!-- Src dropdown -->
                <div class="space-y-1">
                  <button
                    @click="toggleSubCategory('SENTRAFE-Src')"
                    class="nav-link w-full justify-between text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    <span>Src</span>
                    <ChevronDownIcon 
                      v-if="!expandedSubCategories.includes('SENTRAFE-Src')" 
                      class="w-3 h-3 transition-transform" 
                    />
                    <ChevronUpIcon 
                      v-else 
                      class="w-3 h-3 transition-transform" 
                    />
                  </button>
                  
                  <div v-if="expandedSubCategories.includes('SENTRAFE-Src')" class="ml-4 space-y-1">
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-overview' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-overview' }"
                    >
                      Src Overview
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-api' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-api' }"
                    >
                      Api
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-assets' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-assets' }"
                    >
                      Assets
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-components' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-components' }"
                    >
                      Components
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-guards' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-guards' }"
                    >
                      Guards
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-i18n' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-i18n' }"
                    >
                      I18n
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-layouts' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-layouts' }"
                    >
                      Layouts
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-mixins' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-mixins' }"
                    >
                      Mixins
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-router' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-router' }"
                    >
                      Router
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-store' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-store' }"
                    >
                      Store
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-utils' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-utils' }"
                    >
                      Utils
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sentrafe-src-views' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sentrafe-src-views' }"
                    >
                      Views
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sync Engine Section -->
            <div class="space-y-1">
              <button
                @click="toggleSubCategory('Sync Engine')"
                class="nav-link w-full justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span>Sync Engine</span>
                <ChevronDownIcon 
                  v-if="!expandedSubCategories.includes('Sync Engine')" 
                  class="w-4 h-4 transition-transform" 
                />
                <ChevronUpIcon 
                  v-else 
                  class="w-4 h-4 transition-transform" 
                />
              </button>
              
              <div v-if="expandedSubCategories.includes('Sync Engine')" class="ml-4 space-y-1">
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-overview' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-overview' }"
                >
                  Overview
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-structure' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-structure' }"
                >
                  Structure
                </router-link>
                
                <!-- App dropdown -->
                <div class="space-y-1">
                  <button
                    @click="toggleSubCategory('Sync-App')"
                    class="nav-link w-full justify-between text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    <span>App</span>
                    <ChevronDownIcon 
                      v-if="!expandedSubCategories.includes('Sync-App')" 
                      class="w-3 h-3 transition-transform" 
                    />
                    <ChevronUpIcon 
                      v-else 
                      class="w-3 h-3 transition-transform" 
                    />
                  </button>
                  
                  <div v-if="expandedSubCategories.includes('Sync-App')" class="ml-4 space-y-1">
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-overview' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-overview' }"
                    >
                      App Overview
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-api' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-api' }"
                    >
                      Api
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-core' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-core' }"
                    >
                      Core
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-database' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-database' }"
                    >
                      Database
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-models' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-models' }"
                    >
                      Models
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-monitoring' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-monitoring' }"
                    >
                      Monitoring
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-optimizations' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-optimizations' }"
                    >
                      Optimizations
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-processors' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-processors' }"
                    >
                      Processors
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-services' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-services' }"
                    >
                      Services
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-sync' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-sync' }"
                    >
                      Sync
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-utils' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-utils' }"
                    >
                      Utils
                    </router-link>
                    <router-link
                      :to="{ name: 'documentation-section', params: { section: 'sync-engine-app-workers' } }"
                      class="nav-link text-xs"
                      :class="{ 'active': route.params.section === 'sync-engine-app-workers' }"
                    >
                      Workers
                    </router-link>
                  </div>
                </div>
                
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-init-scripts' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-init-scripts' }"
                >
                  Init-Scripts
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-logs' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-logs' }"
                >
                  Logs
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-mongo-init' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-mongo-init' }"
                >
                  Mongo-Init
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-scripts' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-scripts' }"
                >
                  Scripts
                </router-link>
                <router-link
                  :to="{ name: 'documentation-section', params: { section: 'sync-engine-tests' } }"
                  class="nav-link text-sm"
                  :class="{ 'active': route.params.section === 'sync-engine-tests' }"
                >
                  Tests
                </router-link>
              </div>
            </div>
            
          </div>
        </div>

      </div>
    </nav>
  </aside>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    HomeIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    DocumentTextIcon,
    ChevronUpIcon,
  } from '@heroicons/vue/24/outline'
  import { useDocumentationStore } from '@/stores/documentation'


  // Props
  interface Props {
    isCollapsed?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    isCollapsed: false,
  })

  // Emits
  defineEmits<{
    toggle: [isCollapsed: boolean]
  }>()

  // Store
  const documentationStore = useDocumentationStore()

  // Route
  const route = useRoute()

  // Reactive state
  const expandedCategories = ref<string[]>([])
  const expandedSubCategories = ref<string[]>([])
  
  // Methods
  const toggleCategory = (categoryTitle: string) => {
    const index = expandedCategories.value.indexOf(categoryTitle)
    if (index > -1) {
      expandedCategories.value.splice(index, 1)
    } else {
      expandedCategories.value.push(categoryTitle)
    }
  }

  const toggleSubCategory = (subCategoryTitle: string) => {
    const index = expandedSubCategories.value.indexOf(subCategoryTitle)
    if (index > -1) {
      expandedSubCategories.value.splice(index, 1)
    } else {
      expandedSubCategories.value.push(subCategoryTitle)
    }
  }

  // Computed
  const documentation = computed(() => documentationStore.documentation)
  
  // Lifecycle
  onMounted(async () => {
    // Load documentation categories if not loaded yet
    if (documentation.value.length === 0) {
      await documentationStore.loadDocumentationFromMarkdown()
    }

    // Auto-expand categories based on current route
    if (route.path.startsWith('/docs/documentation')) {
      expandedCategories.value = ['Documentation']
    } else if (route.path.startsWith('/docs/information')) {
      expandedCategories.value = ['Information']
    } else if (route.path.startsWith('/docs/standards')) {
      expandedCategories.value = ['Standards']
    }
  })

  // Watch for route changes to auto-expand appropriate categories
  watch(
    () => route.path,
    (newPath) => {
      if (newPath.startsWith('/docs/documentation')) {
        if (!expandedCategories.value.includes('Documentation')) {
          expandedCategories.value.push('Documentation')
        }
      } else if (newPath.startsWith('/docs/information')) {
        if (!expandedCategories.value.includes('Information')) {
          expandedCategories.value.push('Information')
        }
      } else if (newPath.startsWith('/docs/standards')) {
        if (!expandedCategories.value.includes('Standards')) {
          expandedCategories.value.push('Standards')
        }
      }
    },
    { immediate: true }
  )
</script>

<style scoped>
  .nav-link {
    @apply flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200;
  }

  .nav-link-active {
    @apply bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100;
  }

  .nav-link-inactive {
    @apply text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white;
  }

  .active {
    @apply bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100;
  }
</style>
